/**
 * Circuit Breaker
 *
 * Tracks auth and network failure counts with time-windowed resets.
 * When failures exceed thresholds, the circuit opens to prevent
 * cascading failures.
 */

// Circuit breaker for handling repeated failures
export interface CircuitBreakerState {
  authFailures: number;
  networkFailures: number;
  lastAuthFailure: number;
  lastNetworkFailure: number;
  isOpen: boolean;
}

export const circuitBreaker: CircuitBreakerState = {
  authFailures: 0,
  networkFailures: 0,
  lastAuthFailure: 0,
  lastNetworkFailure: 0,
  isOpen: false,
};

export const MAX_AUTH_FAILURES = 15;
export const MAX_NETWORK_FAILURES = 5;
export const AUTH_FAILURE_WINDOW = 120000; // 2 minutes
export const NETWORK_FAILURE_WINDOW = 30000; // 30 seconds
export const CIRCUIT_RESET_DELAY = 30000; // 30 seconds

/**
 * Atomic circuit breaker operations to prevent race conditions
 * These functions perform check-and-update as a single logical operation
 */
export function recordAuthFailure(now: number): number {
  // Atomically check window and increment - prevents lost updates
  if (now - circuitBreaker.lastAuthFailure > AUTH_FAILURE_WINDOW) {
    circuitBreaker.authFailures = 0;
  }
  circuitBreaker.authFailures++;
  circuitBreaker.lastAuthFailure = now;
  return circuitBreaker.authFailures;
}

export function recordNetworkFailure(now: number): number {
  // Atomically check window and increment - prevents lost updates
  if (now - circuitBreaker.lastNetworkFailure > NETWORK_FAILURE_WINDOW) {
    circuitBreaker.networkFailures = 0;
  }
  circuitBreaker.networkFailures++;
  circuitBreaker.lastNetworkFailure = now;
  return circuitBreaker.networkFailures;
}

export function resetAuthFailures(): void {
  circuitBreaker.authFailures = 0;
}

export function resetNetworkFailures(): void {
  circuitBreaker.networkFailures = 0;
}

